import { useState, useEffect } from "react";
import type { AppStage, Theme, FontPairing } from "@structurify/shared-types";

interface ChromeProps {
  theme: Theme;
  fonts: FontPairing;
  stage: AppStage;
  setStage: (s: AppStage) => void;
  authed: boolean;
  userEmail?: string | undefined;
  creditsUsed: number;
  freeLimit: number;
  onAccount: () => void;
  onSignIn: () => void;
}

const STAGE_LABELS: Record<AppStage, string> = {
  upload: "Upload",
  analyze: "Analyze",
  confirm: "Confirm",
  review: "Review",
  export: "Export",
  workflow: "Save workflow",
  returning: "Workspace",
  bulk: "Select files",
  "batch-result": "Results",
  "batch-intel": "Intelligence",
  camera: "Scan",
  audit: "Audit log",
  signin: "Sign in",
  signup: "Sign up",
};

const DOC_FLOW: AppStage[] = ["upload", "analyze", "confirm", "review", "export"];
const BULK_FLOW: AppStage[] = ["bulk", "batch-result", "batch-intel"];

function getFlow(stage: AppStage): AppStage[] | null {
  if (DOC_FLOW.includes(stage)) return DOC_FLOW;
  if (BULK_FLOW.includes(stage)) return BULK_FLOW;
  return null;
}

function crumbContext(stage: AppStage): string {
  if (stage === "returning") return "Workspace";
  if (stage === "signin" || stage === "signup") return "Account";
  if (BULK_FLOW.includes(stage)) return "Bulk run";
  if (stage === "audit") return "Audit log";
  if (stage === "camera") return "Scan";
  if (stage === "workflow") return "Save workflow";
  return "Document";
}

export function Chrome({ theme, fonts, stage, setStage, authed, userEmail, creditsUsed, freeLimit, onAccount, onSignIn }: ChromeProps) {
  const remaining = Math.max(0, freeLimit - creditsUsed);
  const showCreditPill = !authed && stage !== "signin" && stage !== "signup";
  const flow = getFlow(stage);
  const flowIdx = flow ? flow.indexOf(stage) : -1;
  const canPrev = flow !== null && flowIdx > 0;
  const canNext = flow !== null && flowIdx < flow.length - 1;

  return (
    <div style={{ borderBottom: `1px solid ${theme.rule}`, background: theme.surface, flexShrink: 0 }}>
      {/* Top row: logo + breadcrumb + account */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", padding: "10px clamp(14px, 3vw, 28px)", gap: "4px 8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", flexShrink: 0 }}
            onClick={() => setStage(authed || creditsUsed > 0 ? "returning" : "upload")}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <rect x="2" y="2" width="14" height="14" fill="none" stroke={theme.ink} strokeWidth="1.25" />
              <line x1="2" y1="6.5" x2="16" y2="6.5" stroke={theme.ink} strokeWidth="0.75" />
              <line x1="2" y1="11.5" x2="16" y2="11.5" stroke={theme.ink} strokeWidth="0.75" />
              <line x1="6.5" y1="2" x2="6.5" y2="16" stroke={theme.ink} strokeWidth="0.75" />
            </svg>
            <span style={{ fontFamily: fonts.sans, fontSize: 16, fontWeight: 500, letterSpacing: "-0.01em", color: theme.ink }}>Structurify</span>
          </div>
          <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.06em", flexShrink: 0 }}>/</span>
          <span style={{ fontFamily: fonts.sans, fontSize: 12.5, color: theme.inkSoft, fontWeight: 450, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {crumbContext(stage)}
          </span>
          {stage !== "returning" && stage !== "upload" && (
            <>
              <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, flexShrink: 0 }}>·</span>
              <span style={{ fontFamily: fonts.mono, fontSize: 10, color: theme.inkMute, letterSpacing: "0.08em", textTransform: "uppercase", flexShrink: 0 }}>
                {STAGE_LABELS[stage]}
              </span>
            </>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "flex-end" }}>
          {showCreditPill && (
            <button
              onClick={onAccount}
              style={{ padding: "5px 8px", fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em", color: remaining <= 1 ? theme.confidenceLow : theme.inkSoft, background: "transparent", border: `1px solid ${remaining <= 1 ? theme.confidenceLow : theme.rule}`, cursor: "pointer", whiteSpace: "nowrap" }}
            >
              {remaining}/{freeLimit} FREE LEFT
            </button>
          )}
          {!authed && (
            <button
              onClick={onSignIn}
              style={{ fontFamily: fonts.sans, fontSize: 12.5, fontWeight: 500, color: theme.ink, background: "transparent", border: "none", cursor: "pointer", padding: "6px 4px", whiteSpace: "nowrap" }}
            >
              Sign in
            </button>
          )}
          <button
            onClick={onAccount}
            style={{ width: 28, height: 28, borderRadius: 28, background: authed ? theme.ink : "transparent", color: authed ? theme.docPaper : theme.inkSoft, border: authed ? "none" : `1px solid ${theme.rule}`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: fonts.sans, fontSize: 11, fontWeight: 500, cursor: "pointer", flexShrink: 0 }}
          >
            {authed ? (userEmail?.split("@")[0]?.slice(0, 1)?.toUpperCase() ?? "?") : "?"}
          </button>
        </div>
      </div>

      {/* Flow bar — only shown when inside a multi-step flow */}
      {flow && (
        <FlowBar
          flow={flow} stage={stage} setStage={setStage}
          canPrev={canPrev} canNext={canNext}
          theme={theme} fonts={fonts}
        />
      )}
    </div>
  );
}

function FlowBar({ flow, stage, setStage, canPrev, canNext, theme, fonts }: {
  flow: AppStage[];
  stage: AppStage;
  setStage: (s: AppStage) => void;
  canPrev: boolean;
  canNext: boolean;
  theme: Theme;
  fonts: FontPairing;
}) {
  const flowIdx = flow.indexOf(stage);
  const [showLabels, setShowLabels] = useState(window.innerWidth >= 520);

  useEffect(() => {
    const h = () => setShowLabels(window.innerWidth >= 520);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  return (
    <div style={{ display: "flex", alignItems: "center", padding: "0 clamp(14px, 3vw, 28px) 10px", minWidth: 0, overflow: "hidden" }}>
      {/* Prev arrow */}
      <button
        onClick={() => canPrev && setStage(flow[flowIdx - 1]!)}
        disabled={!canPrev}
        style={{ background: "transparent", border: "none", cursor: canPrev ? "pointer" : "default", color: canPrev ? theme.inkSoft : theme.rule, padding: "4px 10px 4px 0", flexShrink: 0, display: "flex", alignItems: "center", transition: "color 150ms" }}
        aria-label="Previous step"
      >
        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M8 2L4 6L8 10" stroke="currentColor" strokeWidth="1.25" fill="none" strokeLinecap="round" /></svg>
      </button>

      {/* Steps and connectors as flat siblings so connectors flex and steps never clip */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", minWidth: 0 }}>
        {flow.flatMap((s, i) => {
          const isCurrent = s === stage;
          const isPast = i < flowIdx;
          const elements = [
            <button
              key={s}
              onClick={() => setStage(s)}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", padding: "2px 0", flexShrink: 0 }}
            >
              <div style={{
                width: 16, height: 16, borderRadius: 16,
                border: `1.5px solid ${isCurrent ? theme.ink : isPast ? theme.inkSoft : theme.rule}`,
                background: isCurrent ? theme.ink : isPast ? theme.inkSoft : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0, transition: "all 200ms",
              }}>
                {isPast ? (
                  <svg width="8" height="8" viewBox="0 0 8 8">
                    <path d="M1.5 4L3.5 6L6.5 2" stroke={theme.surface} strokeWidth="1.25" fill="none" strokeLinecap="round" />
                  </svg>
                ) : isCurrent ? (
                  <div style={{ width: 5, height: 5, borderRadius: 5, background: theme.surface }} />
                ) : null}
              </div>
              {showLabels && (
                <span style={{
                  fontFamily: fonts.mono, fontSize: 10, letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: isCurrent ? theme.ink : isPast ? theme.inkSoft : theme.inkMute,
                  fontWeight: isCurrent ? 600 : 400,
                  whiteSpace: "nowrap",
                  transition: "color 200ms",
                }}>
                  {STAGE_LABELS[s]}
                </span>
              )}
            </button>,
          ];
          if (i < flow.length - 1) {
            elements.push(
              <div
                key={`c${i}`}
                style={{ flex: 1, minWidth: 8, height: 1, background: isPast ? theme.inkSoft : theme.rule, margin: "0 8px", opacity: 0.35 }}
              />,
            );
          }
          return elements;
        })}
      </div>

      {/* Next arrow */}
      <button
        onClick={() => canNext && setStage(flow[flowIdx + 1]!)}
        disabled={!canNext}
        style={{ background: "transparent", border: "none", cursor: canNext ? "pointer" : "default", color: canNext ? theme.inkSoft : theme.rule, padding: "4px 0 4px 10px", flexShrink: 0, display: "flex", alignItems: "center", transition: "color 150ms" }}
        aria-label="Next step"
      >
        <svg width="12" height="12" viewBox="0 0 12 12"><path d="M4 2L8 6L4 10" stroke="currentColor" strokeWidth="1.25" fill="none" strokeLinecap="round" /></svg>
      </button>
    </div>
  );
}
